import { CursorClickIcon, XIcon } from '@heroicons/react/outline';
import { FaWhatsapp } from 'react-icons/fa6';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import type { DemoBrand } from '../types';
import { useChannelsT } from '../useChannelsT';
import { CheckGlyph, delay, tone } from '../../primitives';

/**
 * The "Connect WhatsApp" window from the agent's Channels page, as it is,
 * with the connected number it ends in.
 */
export default function WhatsAppConnect({ brand }: { brand: DemoBrand }) {
  const { t } = useChannelsT();

  return (
    <div className='relative mx-auto max-w-[30rem] pb-4 sm:pb-16'>
      <div
        className={clsxm(
          'relative overflow-hidden rounded-[22px] p-6 text-start shadow-[0_40px_90px_-35px_rgba(30,20,90,0.55)]',
          tone.surface,
          tone.line,
        )}
      >
        <div className='flex items-start justify-between gap-3'>
          <p className={clsxm('text-lg font-semibold', tone.ink)}>
            {t('Connect WhatsApp')}
          </p>
          <XIcon className={clsxm('h-5 w-5 flex-none', tone.quiet)} />
        </div>
        <p className={clsxm('mt-3 text-sm leading-6', tone.body)}>
          {t(
            'A Meta window will open: sign in with Facebook, then pick or create the WhatsApp Business number to connect.',
          )}
        </p>
        <div className='relative mt-5'>
          <span className='flex w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] px-4 py-3 text-sm font-semibold text-white'>
            {t('Continue with Facebook')}
          </span>
          <CursorClickIcon
            className={clsxm(
              styles.float,
              'absolute -bottom-4 end-10 h-7 w-7 text-[#10101c] drop-shadow-[0_2px_2px_rgba(255,255,255,0.9)] dark:text-white dark:drop-shadow-[0_2px_2px_rgba(0,0,0,0.6)]',
            )}
          />
        </div>
        <span
          className={clsxm(
            'mt-3 flex w-full items-center justify-center rounded-xl px-4 py-3 text-center text-sm font-medium ring-1 ring-inset ring-[#0f0f28]/15 dark:ring-white/15',
            tone.ink,
          )}
        >
          {t('I already use the WhatsApp Business app')}
        </span>
        <p className={clsxm('mt-2.5 text-xs leading-5', tone.quiet)}>
          {t(
            'Connect the number from your WhatsApp Business app and keep using the app as usual. Messages work in both places.',
          )}
        </p>
      </div>

      <div
        className={clsxm(
          styles.rv,
          'relative mt-4 flex items-center gap-3 rounded-2xl p-3.5 pe-4 text-start shadow-[0_30px_60px_-28px_rgba(30,20,90,0.6)] sm:absolute sm:-bottom-2 sm:end-[-1.5rem] sm:mt-0 sm:w-[21.5rem]',
          tone.surface,
          tone.line,
        )}
        style={delay(450)}
      >
        <span className='flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-[#25D366]/10'>
          <FaWhatsapp className='h-5 w-5 text-[#25D366]' />
        </span>
        <span className='min-w-0 flex-1'>
          <span
            className={clsxm(
              'block text-sm font-semibold leading-tight sm:truncate',
              tone.ink,
            )}
          >
            {brand.name}
          </span>
          <span className={clsxm('block text-xs', tone.quiet)}>
            <span dir='ltr' className='tabular-nums'>
              +1 555-0142
            </span>
          </span>
        </span>
        <span className='flex flex-none items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300'>
          <CheckGlyph className='h-3.5 w-3.5' />
          {t('Connected')}
        </span>
      </div>
    </div>
  );
}
